import React, { useRef } from 'react';
import { string, bool, func, node } from 'prop-types';
import classNames from 'classnames';
import styles from './DesktopMenuItem.scss';
import { notifyAnalytics } from '../notify-analytics';
import { formatTestId } from '../../../utils/test-id/format-testid';
import {
  offersCategoryDesktop,
  blackFridayCategory,
} from '../offers-category-desktop';

export const DesktopMenuItem = ({
  label,
  url,
  active,
  onClose,
  onSelect,
  isTouch,
  children = undefined,
}) => {
  const isOffer = offersCategoryDesktop(label);
  const isBlackFriday = blackFridayCategory(label);
  const flyoutDelayDuration = 75;
  const handleKeyDown = e => {
    if (e.key !== 'Enter') return;

    if (children) {
      e.preventDefault();
      (active ? onClose : onSelect)();
    } else {
      notifyAnalytics(label);
    }
  };

  const handleOnClick = e => {
    if (!active && children) {
      e.preventDefault();
      onSelect();
    } else {
      notifyAnalytics(label);
    }
  };

  const timeoutRef = useRef(null);
  const liRef = useRef(null);

  const onDelayedSelect = () => {
    timeoutRef.current = setTimeout(onSelect, flyoutDelayDuration);
  };

  const onDelayedMouseLeave = e => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    // Check if the mouse is still within the <li> element or its (injected) children...
    if (
      liRef.current &&
      e.relatedTarget &&
      e.relatedTarget.nodeType === Node.ELEMENT_NODE && // to cope with jsdom in unit test
      liRef.current.contains(e.relatedTarget)
    ) {
      return; // Yes it is - don't close the flyout
    }

    onClose(); // No it isn't - close the flyout
  };

  const onMouseEnterHandlers = {
    onMouseEnter: onDelayedSelect,
    onKeyDown: handleKeyDown,
  };

  const onMouseLeaveHandlers = {
    onMouseLeave: onDelayedMouseLeave,
  };
  const cn = classNames(styles.desktopMenuItem, {
    [styles.desktopMenuItem__active]: active,
  });

  return (
    <li
      className={cn}
      ref={liRef}
      {...(!isTouch && { ...onMouseLeaveHandlers })}
      {...(!isTouch && { ...onMouseEnterHandlers })}
      data-testid={formatTestId(label, 'nav-')}
      suppressHydrationWarning
    >
      <a
        className={classNames(styles.desktopMenuItemLink, {
          [styles.offersLabel]: isOffer || isBlackFriday,
        })}
        href={url}
        onClick={handleOnClick}
        id={formatTestId(label, 'desktop-menu-item-')}
        suppressHydrationWarning
      >
        {label}
      </a>
      {active && children}
    </li>
  );
};

DesktopMenuItem.propTypes = {
  label: string.isRequired,
  url: string.isRequired,
  active: bool.isRequired,
  onSelect: func.isRequired,
  onClose: func.isRequired,
  isTouch: bool.isRequired,
  children: node,
};
